import React from 'react';
import { calculateBezierPath, getAnchorPoint, generateArrowMarkerId } from './utils';
import { ARROW_STYLES } from '../../utils/constants';

export interface CustomArrowProps {
  startId: string;
  endId: string;
  startRect: { x: number; y: number; width: number; height: number };
  endRect: { x: number; y: number; width: number; height: number };
  startAnchor?: 'top' | 'bottom' | 'left' | 'right';
  endAnchor?: 'top' | 'bottom' | 'left' | 'right';
  color?: string;
  strokeWidth?: number;
  headSize?: number;
  curveness?: number;
  dashness?: boolean;
  variant?: 'normal' | 'archived';
  priority?: 'low' | 'medium' | 'high' | 'critical';
}

const CustomArrow: React.FC<CustomArrowProps> = ({
  startId,
  endId,
  startRect,
  endRect,
  startAnchor = 'bottom',
  endAnchor = 'left',
  color = '#007bff',
  strokeWidth = 2,
  headSize = 6,
  curveness = 0.8,
  dashness = false,
  variant = 'normal',
  priority = 'low'
}) => {
  // Apply priority-based styling if variant is normal
  const priorityStyle = variant === 'normal' && priority ? ARROW_STYLES.PRIORITY[priority] : null;
  const finalColor = priorityStyle?.color || color;
  const finalStrokeWidth = priorityStyle?.strokeWidth || strokeWidth;
  const finalHeadSize = priorityStyle?.headSize || headSize;
  const finalOpacity = priorityStyle?.opacity || (variant === 'archived' ? 0.7 : 1);

  const startPoint = getAnchorPoint(startRect, startAnchor);
  const endPoint = getAnchorPoint(endRect, endAnchor);
  const path = calculateBezierPath(startPoint, endPoint, startAnchor, endAnchor, curveness);
  const markerId = generateArrowMarkerId(startId, endId, variant);

  // Increased by 30% (1.5 * 1.3 = 1.95) to better match thicker arrow bodies
  const markerSize = finalHeadSize * 1.95;
  // Adjust refX to account for stroke width so tip extends beyond the arrow body
  const markerRefX = markerSize - (finalStrokeWidth / 2) - 2;

  return (
    <g className="custom-arrow">
      <defs>
        <marker
          id={markerId}
          markerWidth={markerSize}
          markerHeight={markerSize}
          refX={markerRefX}
          refY={markerSize / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={`M 0,0 L ${markerSize},${markerSize / 2} L 0,${markerSize} L ${markerSize * 0.3},${markerSize / 2} z`}
            style={{ fill: finalColor }}
            opacity={finalOpacity}
          />
        </marker>
      </defs>
      <path
        d={path}
        style={{ stroke: finalColor }}
        strokeWidth={finalStrokeWidth}
        fill="none"
        markerEnd={`url(#${markerId})`}
        strokeDasharray={dashness ? `${finalStrokeWidth * 2.5},${finalStrokeWidth * 2.5}` : undefined}
        opacity={finalOpacity}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
};

export default CustomArrow;